import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useSlotGroup,
  usePage,
  EditableSlotGroup,
  RichTextContent,
} from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';

// Convert a page alias (kebab-case slug, e.g. "floor-plans") into a
// title-cased friendly name (e.g. "Floor Plans") so the eyebrow can
// echo the page name without the component needing access to the raw
// `name` field from pages.json. PageContext only carries `alias`,
// `path`, `title` (SEO), and `description`, so deriving from `alias`
// is the cleanest non-vendor-modifying route.
function pageTitleFromAlias(alias: string): string {
  return alias
    .split('-')
    .filter(Boolean)
    .map(part => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

interface HeroSolidBGProps {
  tokenPrefix?: string;
  title?: string;
  eyebrow?: string;
  blurbField?: string;
  defaultText?: string;
  // Per-page default sources for the two stacked photos. Each page that
  // uses this hero can point the large / small image at the collection
  // most relevant to its content (e.g. amenities → `amenities`,
  // floor plans → `floorplan_groups`, neighborhood → `neighborhood_pois`).
  // When omitted, both defaults fall back to `photo_gallery` indices 0
  // and 1 — the canonical lifestyle pool, which is what the static
  // contentSchema declares.
  image1Collection?: string;
  image1Index?: number;
  image2Collection?: string;
  image2Index?: number;
}

// Per-collection mapping of "which field on each item holds the image
// URL" and "which field holds its alt text". Drives the per-page
// runtime override of the collage slot group defaults. Limited to the
// collections that carry image fields on the canonical Vibesites
// property schema — adding a new collection here is the one-line way
// to make it bindable from pages.json.
const COLLECTION_IMAGE_FIELDS: Record<
  string,
  { image: string; alt: string }
> = {
  photo_gallery: { image: 'image', alt: 'alt_text' },
  amenities: { image: 'amenity_image', alt: 'amenity_image_alt_text' },
  floorplans: { image: 'floorplan_image_url', alt: 'floorplan_name' },
  floorplan_groups: {
    image: 'floorplan_group_image_url',
    alt: 'floorplan_group_image_alt',
  },
  virtual_tours: {
    image: 'virtual_tour_thumbnail',
    alt: 'virtual_tour_title',
  },
  neighborhood_pois: { image: 'poi_featured_image', alt: 'poi_name' },
};

// Build a runtime SlotGroupDefinition for a per-page collage image.
// Falls back to the canonical photo_gallery default (matches the
// static contentSchema entry) when the page passes no override or
// passes a collection name we don't recognize. The fallback `label`
// is supplied by the caller so the editor sidebar still shows
// "Large Image" / "Small Image (Overlap)" for each slot.
function buildCollageDef(
  label: string,
  collection: string | undefined,
  index: number | undefined,
  fallbackIndex: number
): SlotGroupDefinition {
  const safeCollection =
    collection && COLLECTION_IMAGE_FIELDS[collection] ? collection : 'photo_gallery';
  const fieldMap = COLLECTION_IMAGE_FIELDS[safeCollection];
  const safeIndex =
    typeof index === 'number' && index >= 0 ? index : fallbackIndex;
  return {
    label,
    fields: {
      image: {
        label: 'Image',
        type: 'image',
        defaultField: `${safeCollection}[${safeIndex}].${fieldMap.image}`,
      },
      alt: {
        label: 'Alt Text',
        type: 'text',
        defaultField: `${safeCollection}[${safeIndex}].${fieldMap.alt}`,
        defaultValue: 'Property lifestyle photo',
      },
    },
    sourceBinding: {
      collection: safeCollection,
      defaultIndex: safeIndex,
      fieldMappings: { image: fieldMap.image, alt: fieldMap.alt },
    },
  };
}

// Two sibling image slot groups for the stacked-photo composition on the
// left side. Each one is its own module-level literal so the schema
// linter can extract them statically (no spreads of non-literals
// allowed). Each collage slot pulls a DIFFERENT default photo so a
// freshly imported property lights up with two distinct images instead
// of two copies of the featured photo. The `sourceBinding` declares the
// collection + index so the editor's "reset to default" picks the right
// gallery item, and `defaultField` mirrors the same path so the runtime
// can resolve a default without an explicit user pick. Indices are
// staggered 0/1 so the large / small photos each show a separate
// gallery entry.
const collageImage1Def: SlotGroupDefinition = {
  label: 'Large Image',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultField: 'photo_gallery[0].image',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultField: 'photo_gallery[0].alt_text',
      defaultValue: 'Property lifestyle photo',
    },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 0,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
};

const collageImage2Def: SlotGroupDefinition = {
  label: 'Small Image (Overlap)',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultField: 'photo_gallery[1].image',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultField: 'photo_gallery[1].alt_text',
      defaultValue: 'Property lifestyle photo',
    },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 1,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
};

// Floor-plans cross-link CTA — rendered below the hero blurb on every
// page that uses this component EXCEPT the floor-plans page itself
// (suppressed automatically by checking `usePage().alias`, so no
// per-page wiring is required in pages.json). Editors can override
// the URL, label, and target per page via the standard slot-group
// inline editor; the defaults point at `/floor-plans` with the copy
// the user requested. `_target` keeps the leading underscore so the
// runtime treats it as a meta field rather than a content slot.
const floorplansCtaDef: SlotGroupDefinition = {
  label: 'Browse Floor Plans CTA',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/floor-plans' },
    label: { label: 'Label', type: 'text', defaultValue: 'Browse Our Floor Plans' },
    _target: { label: 'Link Target', type: 'text', defaultValue: '_self' },
  },
};

export function HeroSolidBG({
  tokenPrefix,
  title = 'Page',
  eyebrow,
  blurbField,
  defaultText,
  image1Collection,
  image1Index,
  image2Collection,
  image2Index,
}: HeroSolidBGProps) {
  const prefix = tokenPrefix || 'herosolidbg';
  const eyebrowPath = `${prefix}.eyebrow`;
  const titlePath = `${prefix}.title`;
  const blurbPath = `${prefix}.blurb`;
  const collage1Path = `${prefix}.collage_image_1`;
  const collage2Path = `${prefix}.collage_image_2`;
  const floorplansCtaPath = `${prefix}.floorplans_cta`;

  // Build per-instance runtime defs from the page props so each page
  // can point its hero photos at a relevant collection (amenities,
  // floor plan groups, neighborhood POIs, virtual tour thumbnails,
  // ...) without forking the component. When no override is supplied
  // these fall back to `photo_gallery[0]` / `photo_gallery[1]` —
  // matching the canonical static `collageImage1Def` /
  // `collageImage2Def` below and the contentSchema export. The
  // static defs still anchor the schema linter; the editor sidebar
  // reads the runtime def via `EditableSlotGroup` for sourceBinding
  // metadata.
  const runtimeCollage1Def = buildCollageDef(
    'Large Image',
    image1Collection,
    image1Index,
    0
  );
  const runtimeCollage2Def = buildCollageDef(
    'Small Image (Overlap)',
    image2Collection,
    image2Index,
    1
  );

  const collage1 = useSlotGroup(collage1Path, runtimeCollage1Def);
  const collage2 = useSlotGroup(collage2Path, runtimeCollage2Def);
  const floorplansCta = useSlotGroup(floorplansCtaPath, floorplansCtaDef);

  // Default eyebrow simply echoes the page name — so the Floor Plans
  // hero reads "Floor Plans" above the H1, the Amenities hero reads
  // "Amenities", etc. The page name is derived from `usePage().alias`
  // because PageContext does not expose the raw `name` field;
  // converting "floor-plans" → "Floor Plans" gives the correct value
  // without depending on a per-page prop or requiring vendor changes.
  // Pages can still override via the `eyebrow` prop or the editor.
  // Passing an explicit empty string (`eyebrow: ""`) is the page-level
  // opt-out — it hides the eyebrow row entirely so the title sits
  // flush against the top of the copy column. Anything else (undefined
  // or a real string) keeps the eyebrow visible with either the
  // default or the override.
  const page = usePage();
  const hideEyebrow = eyebrow === '';
  const defaultEyebrow = eyebrow ?? pageTitleFromAlias(page.alias);
  // The "Browse Our Floor Plans" cross-link CTA shows on every page
  // that uses this hero EXCEPT the floor-plans page itself — sending
  // a visitor from `/floor-plans` to `/floor-plans` would be a
  // no-op. Checking the page alias here means we don't have to wire
  // a per-page `showFloorPlansCTA` prop in pages.json — the
  // suppression is automatic and stays correct if a future page
  // adopts HeroSolidBG.
  const hideFloorplansCta = page.alias === 'floor-plans';

  return (
    <section className="bg-primary text-primary-foreground pt-14 pb-8 md:pt-20 md:pb-12 relative overflow-hidden">
      {/* `lg:ml-0 lg:mr-auto` anchors the inner container to the
          LEFT edge of the section at lg+ (instead of `mx-auto`'s
          horizontal centering) so the whole section's content —
          photo collage AND copy column — sits noticeably further
          left in the viewport. Mobile / sm / md keep `mx-auto` so
          the stacked single-column layout stays centered.
          Max-width bumped `max-w-6xl` → `max-w-7xl` (72rem → 80rem)
          so the inner container reaches further to the right; since
          the block is left-anchored on lg+, that extra 8rem of
          width lands on the right side and feeds the copy column
          (and the blurb inside it) noticeably more horizontal
          breathing room. */}
      <div className="max-w-7xl mx-auto lg:ml-0 lg:mr-auto px-4 sm:px-6 lg:px-8">
        {/* Asymmetric two-column grid on lg+: left column (photo
            collage) wider than the right (copy) — `6fr_5fr` ≈
            55/45 — so the collage still reads as the visual
            centerpiece while the copy column gets a meaningful
            share of the row for the blurb to breathe. (Was
            `7fr_5fr` ≈ 58/42; nudged toward the right column at
            explicit request so the blurb has more horizontal real
            estate; combined with the `max-w-6xl` → `max-w-7xl`
            bump on the inner container, the blurb gains noticeable
            line-width.) Mobile collapses to `grid-cols-1` so the
            stacked single-column layout keeps full width for
            both. */}
        <div className="grid grid-cols-1 lg:grid-cols-[6fr_5fr] gap-10 lg:gap-16 items-center">

          {/* ---- Left column: two-image stacked composition ----
              Matches the reference (lakehausapartmenthomes.com/floor-plans):
                1. Image 1 — large square photo, anchored top-LEFT,
                   reads as the dominant mass of the composition.
                2. Image 2 — smaller square photo, anchored
                   bottom-RIGHT, overlapping the large photo at its
                   lower-right corner.
              No overlays, tints, or duotone effects — both photos
              show full-color.

              Container uses `aspect-[5/4]` (W:H = 5:4, so height
              is 80% of width) instead of `aspect-square` so the
              bottom edge of the column sits closer to the visible
              composition — eliminating the blue empty zone below
              the photos that read as excess bottom padding. Each
              child image keeps `aspect-square` (sized by its
              `w-[N%]` width) so the photos themselves remain
              perfectly square regardless of the container's aspect.
              (Section also already has `pb-0 md:pb-0`; this is the
              other half of "decrease bottom padding" — collapsing
              the container-internal empty space.) */}
          <div className="relative w-full aspect-[5/4] order-2 lg:order-1">
            {/* Large square — anchored top-left at 68% of container
                width, height auto-matches via `aspect-square`. */}
            <EditableSlotGroup
              groupPath={collage1Path}
              definition={runtimeCollage1Def}
              className="absolute top-0 left-0 w-[68%] aspect-square shadow-lg overflow-hidden"
            >
              <VibesitesImage
                url={collage1.image || ''}
                alt={collage1.alt || ''}
                className="w-full h-full object-cover"
              />
            </EditableSlotGroup>
            {/* Smaller square — 38% width with `aspect-square` so
                height auto-matches. Bottom offset dropped from 20%
                of `aspect-square` → 6% of `aspect-[5/4]` so the
                small image now sits much closer to the container
                bottom, closing the visible blue zone that read as
                bottom padding. Still extends below the large
                image's lower edge for the overlapping-stack look,
                and still sits 6% in from the right edge. */}
            <EditableSlotGroup
              groupPath={collage2Path}
              definition={runtimeCollage2Def}
              className="absolute bottom-[6%] right-[6%] w-[38%] aspect-square shadow-xl overflow-hidden"
            >
              <VibesitesImage
                url={collage2.image || ''}
                alt={collage2.alt || ''}
                className="w-full h-full object-cover"
              />
            </EditableSlotGroup>
          </div>

          {/* Right column: Eyebrow + Title + Blurb.
              `lg:self-start` overrides the grid row's `items-center`
              on this column only so the copy stack anchors to the
              TOP of the row instead of vertically centering against
              the taller photo collage on the left. `lg:-mt-4
              xl:-mt-8` then pulls the copy a small step further UP
              past the row's natural top edge — same device
              `PropertyOverview` uses (it goes further with
              `lg:-mt-10 xl:-mt-16` because its copy is shorter; the
              hero blurb runs longer so the upward pull stays gentle
              — `lg:-mt-1 xl:-mt-2`, just enough to optically align
              the eyebrow with the back photo's top edge without
              floating the copy free of the composition). Mobile
              keeps the default flow so the stacked single-column
              layout never crowds the section's top padding. */}
          <div className="flex flex-col gap-5 order-1 lg:order-2 text-center lg:text-left lg:self-start lg:-mt-1 xl:-mt-2">
            {/* Eyebrow / pre-title — `text-xs sm:text-sm`,
                `font-bold` (matches the requested bolded eyebrow
                treatment; previously `font-semibold`),
                `tracking-[0.2em]`, uppercase. Color is
                `--vibe-color-text-on-primary` since this hero paints
                on a primary background — the on-primary token is the
                palette's contract-bound legible-against-primary
                color, so it stays readable across brand palettes.
                Suppressed entirely when `eyebrow: ""` is passed in so
                pages can opt out of the eyebrow row (otherwise the
                empty `RichTextContent` div would still consume a
                gap-5 slot above the title). */}
            {!hideEyebrow && (
              // Negative top margin pulls the eyebrow closer to the
              // section's top edge on mobile / tablet only — the
              // section's `py-14 md:py-20` gives a tall padding
              // cushion and the `-mt-*` pares back a chunk of it
              // without trimming the section's overall vertical
              // rhythm. Explicitly reset to `lg:mt-0` so the
              // desktop layout keeps its original eyebrow position
              // (the desktop column already uses `lg:-mt-1
              // xl:-mt-2` on its wrapper to optically align the
              // eyebrow with the back photo's top edge; we don't
              // want a second negative margin stacking on top of
              // that). Sibling spacing is untouched because flex
              // `gap` is between items, not at the edges.
              // Boldness is enforced two ways so the eyebrow stays
              // bold regardless of how the rich-text content is
              // sourced:
              //   1. The default value wraps the page-title string
              //      in `<strong>` so fresh installs (no stored
              //      override) render with semantic bolding baked
              //      in — same pattern the floor-plans page used
              //      previously with `<strong>Floor Plans</strong>`.
              //   2. `[&_*]:font-bold` (Tailwind arbitrary
              //      descendant selector) forces every descendant
              //      of the eyebrow `<div>` to font-weight 700.
              //      Necessary because content stored in the DB by
              //      the inline editor sometimes wraps text in a
              //      `<span>` with its own inline font-weight,
              //      which would otherwise win on specificity over
              //      the parent's `font-bold` and leave the visible
              //      text rendering at the inherited / normal
              //      weight. Belt-and-suspenders means the eyebrow
              //      reads bold whether the content comes from the
              //      default, a saved editor override, or freshly
              //      typed rich text.
              <RichTextContent
                path={eyebrowPath}
                defaultValue={`<p><strong>${defaultEyebrow}</strong></p>`}
                as="div"
                className="font-body text-xs sm:text-sm font-bold [&_*]:font-bold uppercase tracking-[0.2em] opacity-90 -mt-4 md:-mt-6 lg:mt-0"
                style={{ color: 'var(--vibe-color-text-on-primary)' }}
              />
            )}
            {/* Title — sized down from the previous hero scale
                (`text-4xl sm:text-5xl md:text-6xl`) to match
                `PropertyOverview`'s h2 (`text-2xl sm:text-3xl
                md:text-4xl`), with the same `leading-tight` and
                without the previous `font-bold` / `tracking-tight`
                so the two headings read as siblings in weight and
                rhythm. */}
            <RichTextContent
              path={titlePath}
              defaultValue={`<p>${title}</p>`}
              as="h1"
              className="font-heading font-bold text-xl sm:text-2xl md:text-3xl leading-tight break-words"
              style={{ color: 'var(--vibe-color-text-on-primary)' }}
            />
            {/* Blurb — sized down from `text-base md:text-lg` to
                `text-sm` to match `PropertyOverview`'s description,
                with the same `leading-relaxed`. */}
            <RichTextContent
              path={blurbPath}
              defaultField={blurbField}
              defaultValue={defaultText || ''}
              as="div"
              className="font-body text-base leading-[1.92] opacity-90 [&_a]:underline"
            />
            {/* "Browse Our Floor Plans" cross-link CTA — pill button
                styled to match `.header-nav-cta` in `Header.tsx` on
                shape, padding, weight, tracking, and radius, but
                with a DIFFERENT hover swap so the pill stays
                legible against this hero's `bg-primary` section
                background:
                  - resting: secondary fill
                    (`--vibe-color-secondary`) with text-on-secondary
                    label — matches the header CTA's resting state.
                  - no border, 2px radius (`rounded-sm`)
                  - `font-heading`, weight 600, tracking 0.05em,
                    text-base (1rem), padding 0.875rem / 2.5rem
                    (`py-3.5 px-10`)
                  - hover: white fill
                    (`--vibe-color-text-on-primary`, the
                    palette's contract-bound legible-against-primary
                    swatch — `#FFFFFF` in the Oasis tokens) with
                    primary-colored label, plus
                    `translateY(-1px)` lift. Deliberately diverges
                    from the header's `hover → primary fill` because
                    THIS section paints on `bg-primary`; flipping
                    the CTA to primary on hover would dissolve the
                    pill into the section background. White-on-
                    primary keeps the hover state high-contrast
                    against the tan section.
                  - active settles back to the resting position.
                Uppercase is preserved (the header CTA renders
                mixed-case "Schedule a Tour" without
                `text-transform`; this CTA's label reads as a
                section action and the user asked to keep caps —
                hence the explicit `uppercase` class here).
                Values are ported via Tailwind utilities + arbitrary
                CSS-var classes rather than reusing the
                `.header-nav-cta` selector, so this component stays
                self-contained (if the header CTA's class ever
                changes under it, the hero CTA doesn't silently
                break). Suppressed on `/floor-plans` (see
                `hideFloorplansCta` above) so the CTA never points
                a visitor back to the page they're already on.
                `self-center lg:self-start` puts the pill in the
                middle of the stack on mobile (matching the
                column's `text-center` rhythm) and against the left
                edge on lg+ (matching `lg:text-left`). */}
            {!hideFloorplansCta && (
              <EditableSlotGroup
                groupPath={floorplansCtaPath}
                definition={floorplansCtaDef}
                className="self-center lg:self-start"
              >
                <a
                  href={floorplansCta.url || '/floor-plans'}
                  target={floorplansCta._target || '_self'}
                  className="inline-flex items-center justify-center gap-2 px-10 py-3.5 text-base font-heading font-semibold uppercase tracking-[0.05em] rounded-sm no-underline whitespace-nowrap transition-[background-color,color,transform] duration-[250ms] ease-out bg-[var(--vibe-color-secondary)] text-[var(--vibe-color-text-on-secondary)] hover:bg-[var(--vibe-color-text-on-primary)] hover:text-[var(--vibe-color-primary)] hover:-translate-y-px active:translate-y-0"
                >
                  {floorplansCta.label || 'Browse Our Floor Plans'}
                </a>
              </EditableSlotGroup>
            )}
          </div>

        </div>
      </div>

      {/* Schema-lint anchors — hidden, never interactive */}
      <RichTextContent
        path="herosolidbg.eyebrow"
        defaultValue=""
        as="span"
        className="hidden"
        style={{ display: 'none' }}
      />
      <RichTextContent
        path="herosolidbg.title"
        defaultValue=""
        as="span"
        className="hidden"
        style={{ display: 'none' }}
      />
      <RichTextContent
        path="herosolidbg.blurb"
        defaultValue=""
        as="span"
        className="hidden"
        style={{ display: 'none' }}
      />
      <EditableSlotGroup
        groupPath="herosolidbg.collage_image_1"
        definition={collageImage1Def}
        className="hidden"
        style={{ display: 'none' }}
      >
        <span />
      </EditableSlotGroup>
      <EditableSlotGroup
        groupPath="herosolidbg.collage_image_2"
        definition={collageImage2Def}
        className="hidden"
        style={{ display: 'none' }}
      >
        <span />
      </EditableSlotGroup>
      <EditableSlotGroup
        groupPath="herosolidbg.floorplans_cta"
        definition={floorplansCtaDef}
        className="hidden"
        style={{ display: 'none' }}
      >
        <span />
      </EditableSlotGroup>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'HeroSolidBG',
  displayName: 'Hero – Solid Background',
  tokens: {
    'herosolidbg.eyebrow': {
      label: 'Eyebrow',
      type: 'richtext',
      defaultValue: '<p>Explore Our Page</p>',
    },
    'herosolidbg.title': {
      label: 'Title',
      type: 'richtext',
      defaultValue: '<p>Page</p>',
    },
    'herosolidbg.blurb': {
      label: 'Page Blurb',
      type: 'richtext',
      defaultValue: '',
    },
  },
  slotGroups: {
    'herosolidbg.collage_image_1': collageImage1Def,
    'herosolidbg.collage_image_2': collageImage2Def,
    'herosolidbg.floorplans_cta': floorplansCtaDef,
  },
};

(HeroSolidBG as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default HeroSolidBG;
